import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { useNavigate, useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import GlobalStyle from '../styles/GlobalStyle';
import Menu from '../components/Menu';
import LogOutView from '../components/LogOutView';
import { useUser } from '../context/UserContext';
import { Container, Wrapper } from '../styles/TableStyle';
import { SmallButton } from '../styles/Buttons';
import { Color, Colors } from '../styles/Colors';
import { exchangeVismaCode, getVismaAuthorizeUrl, getVismaClients } from '../services/api';

// Survives the round trip to Visma since MSAL also keeps its session in sessionStorage.
const STATE_KEY = 'visma_oauth_state';

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 2rem 0;

    select {
        max-width: 300px;
        padding: 0.5rem;
        border-radius: 4px;
    }

    textarea {
        width: 100%;
        min-height: 6rem;
        font-family: monospace;
        padding: 0.5rem;
        border-radius: 4px;
    }
`;

const ErrorText = styled.p`
    color: ${Color(Colors.red)};
`;

const addYears = (iso, years) => {
    const date = new Date(iso);
    date.setFullYear(date.getFullYear() + years);
    return date.toISOString().slice(0, 10);
};

const VismaConnect = () => {
    const { roles } = useUser();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [tokens, setTokens] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const exchangeStarted = useRef(false);

    const hasRole = roles && roles.includes('Visma');
    const code = searchParams.get('code');

    useEffect(() => {
        if (!hasRole) return;
        getVismaClients()
            .then(list => {
                setClients(list);
                if (list.length > 0) setSelectedClient(list[0].key);
            })
            .catch(e => setError(e.message));
    }, [hasRole]);

    // Callback from Visma: verify state, then let the backend exchange the code.
    useEffect(() => {
        if (!hasRole || exchangeStarted.current) return;
        const vismaError = searchParams.get('error');
        if (!code && !vismaError) return;
        exchangeStarted.current = true;

        const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
        sessionStorage.removeItem(STATE_KEY);
        // Drop code/state from the address bar so a reload doesn't reuse them.
        navigate('/visma', { replace: true });

        if (vismaError) {
            setError(`${vismaError}: ${searchParams.get('error_description') || ''}`);
            return;
        }
        if (!saved || saved.state !== searchParams.get('state')) {
            setError('Ogiltigt state - starta om anslutningen.');
            return;
        }

        setLoading(true);
        exchangeVismaCode(saved.client, code)
            .then(setTokens)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [hasRole, code, searchParams, navigate]);

    const connect = async () => {
        setError(null);
        setTokens(null);
        try {
            const state = uuidv4();
            const url = await getVismaAuthorizeUrl(selectedClient, state);
            sessionStorage.setItem(STATE_KEY, JSON.stringify({ state, client: selectedClient }));
            window.location.assign(url);
        } catch (e) {
            setError(e.message);
        }
    };

    return (
        <>
            <GlobalStyle />
            <AuthenticatedTemplate>
                <Container>
                    <div>
                        <Menu />
                    </div>
                    <Wrapper>
                        <div>
                            <h1>Anslut till Visma</h1>
                            {!hasRole ? (
                                <p>Du saknar behörighet (Visma) för detta verktyg.</p>
                            ) : (
                                <Section>
                                    <p>
                                        Logga in i Visma och välj företag för att ta fram en ny refresh token till en integration.
                                        Kopiera token till integrationens konfiguration - den visas bara en gång och sparas inte här.
                                    </p>
                                    <select
                                        value={selectedClient}
                                        onChange={e => setSelectedClient(e.target.value)}
                                        disabled={loading}
                                    >
                                        {clients.map(c => (
                                            <option key={c.key} value={c.key}>{c.name}</option>
                                        ))}
                                    </select>
                                    <SmallButton onClick={connect} disabled={!selectedClient || loading}>
                                        Anslut till Visma
                                    </SmallButton>

                                    {loading && <p>Hämtar token...</p>}
                                    {error && <ErrorText>{error}</ErrorText>}

                                    {tokens && (
                                        <>
                                            <h4>Refresh token ({tokens.client})</h4>
                                            <textarea readOnly value={tokens.refresh_token || ''} onFocus={e => e.target.select()} />
                                            <SmallButton onClick={() => navigator.clipboard.writeText(tokens.refresh_token)}>
                                                Kopiera
                                            </SmallButton>
                                            <p>
                                                Utfärdad: {tokens.issued_at.slice(0, 10)}. Enligt Vismas dokumentation gäller den i två år,
                                                dvs. till ca {addYears(tokens.issued_at, 2)}, eller tills användaren byter lösenord.
                                            </p>
                                            <p>Scope: {tokens.scope}</p>
                                        </>
                                    )}
                                </Section>
                            )}
                        </div>
                    </Wrapper>
                </Container>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <LogOutView
                    message='Genom att logga in kan du ta del av våra verktyg - agenda och rapportgeneratorn'
                />
            </UnauthenticatedTemplate>
        </>
    );
};

export default VismaConnect;
